import { useEffect, useRef, useState } from "react";
import moment from "moment";
import styled from "styled-components";

import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Description,
  InsertDriveFile,
  Autorenew,
  Delete,
  MoreVert,
} from "@material-ui/icons";
import { createPortal } from "react-dom";

export default function TransactionsTable({
  transactions,
  onConsult,
  onRemove,
  type,
  onConsultLog,
  onSendAgain = null,
  loading,
}) {
  return (
    <Table>
      <TitleRow>
        <IdTable>Report ID</IdTable>
        <IdTable>Último evento</IdTable>
        <ConsultTable></ConsultTable>
      </TitleRow>
      {transactions.map((content) => {
        const reportId =
          type === "cash"
            ? content.document.cashAdvanceData.cashAdvanceId
            : content.document.report.reportId;
        const reportDate =
          type === "cash"
            ? content.document.cashAdvanceData?.requestDate
            : content.document.report?.reportPaymentProcessingDate || "";
        const isProcessing =
          type === "cash"
            ? loading === content.document.cashAdvanceData.cashAdvanceId
            : loading === content.document.report.reportId;

        return (
          <ResultRow key={content.id}>
            <IdResult>{reportId}</IdResult>
            <Td>{moment(reportDate).format("DD/MM/YYYY HH:mm")}</Td>
            <Td>
              <RowActions
                onLogs={() =>
                  onConsultLog(
                    type === "cash"
                      ? content.id
                      : content.document.report?.reportId || ""
                  )
                }
                onJson={() => onConsult(content)}
                onProcess={
                  onSendAgain
                    ? () =>
                        onSendAgain(
                          type === "cash"
                            ? content.document.cashAdvanceData.cashAdvanceId
                            : content.document.report.reportId
                        )
                    : null
                }
                processing={isProcessing}
                onRemove={() => onRemove(content.id)}
              />
            </Td>
          </ResultRow>
        );
      })}
    </Table>
  );
}

const MENU_WIDTH = 220;

export const RowActions = ({ onLogs, onJson, onProcess, processing, onRemove }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState({ left: 0, top: 0 });

  useEffect(() => {
    function handleClickOutside(event) {
      const menuNode = menuRef.current;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        (!menuNode || !menuNode.contains(event.target))
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }

    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
      const availableLeft = rect.right - MENU_WIDTH;
      const clampedLeft = Math.min(
        Math.max(availableLeft, 12),
        Math.max(viewportWidth - MENU_WIDTH - 12, 12)
      );
      setPortalStyle({
        left: clampedLeft,
        top: rect.top,
      });
    }

    setOpen(true);
  };

  return (
    <ActionMenuWrapper ref={wrapperRef}>
      <ComboButton onClick={toggleMenu}>
        <MoreVert style={{ fontSize: 18 }} />
        Ações
      </ComboButton>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <ActionMenuPortal
            ref={menuRef}
            className={open ? "open" : ""}
            style={{ left: portalStyle.left, top: portalStyle.top }}
          >
            <ActionMenuItem
              onClick={() => {
                setOpen(false);
                onLogs();
              }}
            >
              <Description style={{ fontSize: 18 }} />
              Logs
            </ActionMenuItem>
            <ActionMenuItem
              onClick={() => {
                setOpen(false);
                onJson();
              }}
            >
              <InsertDriveFile style={{ fontSize: 18 }} />
              JSON
            </ActionMenuItem>
            {onProcess && (
              <ActionMenuItem
                disabled={processing}
                onClick={() => {
                  if (processing) return;
                  setOpen(false);
                  onProcess();
                }}
              >
                {processing ? (
                  <CircularProgress size={16} />
                ) : (
                  <Autorenew style={{ fontSize: 18 }} />
                )}
                {processing ? "Processando..." : "Processar"}
              </ActionMenuItem>
            )}
            <ActionMenuItem
              remove
              onClick={() => {
                setOpen(false);
                onRemove();
              }}
            >
              <Delete style={{ fontSize: 18 }} />
              Remover
            </ActionMenuItem>
          </ActionMenuPortal>,
          document.body
        )}
    </ActionMenuWrapper>
  );
};

const ActionMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: flex-end;
`;

const ComboButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  color: #374151;
  background: #ede9fe;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #ddd6fe;
  }
`;

const ActionMenuPortal = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  min-width: ${MENU_WIDTH}px;
  z-index: 500;
  padding: 6px 0;
  opacity: 0;
  transform: translateY(-100%) translateY(-8px);
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &.open {
    opacity: 1;
    transform: translateY(-100%) translateY(-14px);
    pointer-events: all;
  }
`;

const ActionMenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ remove }) => (remove ? "#dc2626" : "#1f2937")};
  font-family: "Mulish", sans-serif;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(79, 70, 229, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IdResult = styled.td`
  text-align: center;
`;

const ConsultTable = styled.th`
  width: 25%;
`;

const IdTable = styled.th`
`;

const ResultRow = styled.tr`
  border-bottom: 2px solid #e0e0e0;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const TitleRow = styled.tr`
  background-color: #f5f5f5;
`;

const Td = styled.td`
  text-align: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
`;
